import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import DeleteIcon from "@mui/icons-material/DeleteForeverRounded";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { RAIL_LABEL } from "../../../constants/configuration.const";
import DragHandleIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useSnackbar } from "notistack";
import { Button, IconButton, TextField, Tooltip } from "@mui/material";
import CollectionForm, { dynamicValidationSchema } from "./CollectionForm";

const CollectionsContentCurationCard = forwardRef(
    (
        {
            dragHandleProps,
            onClickDelete,
            onClickAdd,
            data,
            hideAddButton,
            handleCollections,
            allowEdit,
            handleRail,
            playlist,
        }: any,
        ref: any
    ) => {
        // hooks
        const [showCollectionList, setShowCollectionList] = useState(false);
        const { enqueueSnackbar } = useSnackbar();
        const collectionListRef = useRef<any>(null);
        const [collectionInfoDraft, setCollectionInfoDraft] =
            useState<any>(null);
        const [errorMessage, setErrorMessage] = useState("");
        const [formChangeKey, setFormChange] = useState<any>(null);
        const [formValid, setFormValid] = useState<any>(null);
        const [playlistState, setPlaylistState] = useState<any>(playlist);

        useEffect(() => {
            setPlaylistState(playlist);
        }, [playlist]);

        useImperativeHandle(
            ref,
            () => ({
                onOpenForm,
                isValid,
                handleSubmit,
                getFormValue,
            }),
            [showCollectionList, formChangeKey, formValid]
        );

        const onOpenForm = () => {
            setShowCollectionList(true);
            setTimeout(() => {
                collectionListRef.current.validateForm();
            }, 100);
        };

        const isValid = () => {
            if (showCollectionList) {
                return collectionListRef.current.isValidForm();
            } else {
                return formValid?.isValid == null ? null : formValid?.isValid;
            }
        };

        const handleSubmit = () => {
            setShowCollectionList(true);
            setTimeout(() => {
                saveCollections();
            }, 100);
        };
        const getFormValue = () =>
            collectionListRef?.current?.getFormValues() || formValid.values;

        const onOpenCollection = () => {
            setShowCollectionList(true);
        };

        const onCloseCollection = () => {
            const { isValidForm, getFormValues } = collectionListRef.current;
            setFormValid({ isValid: isValidForm(), values: getFormValues() });
            setShowCollectionList(false);
            handleRail(getFormValues());
        };

        useEffect(() => {
            if (data) {
                validateInput(data);
            }
        }, [data]);

        const validateInput = async (inputJson: any) => {
            try {
                await dynamicValidationSchema.validate(inputJson);
                setFormValid({ isValid: true, values: inputJson });
            } catch (error) {
                setFormValid({ isValid: false, values: inputJson });
            }
        };

        const saveCollections = () => {
            const { isValidForm, getFormValues } = collectionListRef.current;
            const validForm = isValidForm();
            if (validForm) {
                setFormValid({ isValid: validForm, values: getFormValues() });
                enqueueSnackbar("Collection Added", {
                    variant: "info",
                    autoHideDuration: 2000,
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "center",
                    },
                });
                setCollectionInfoDraft(null);
                setErrorMessage("");
                setShowCollectionList(false);
                collectionListRef.current.handleSubmitFormik();
            } else {
                setErrorMessage("Please fill all the fields");
            }
        };

        return (
            <div
                className={
                    "curation-list-card-container d-flex j-center curation-card"
                }
            >
                <div {...dragHandleProps} className="d-flex float-center">
                    <DragHandleIcon className="drag-handle" />
                </div>
                <div className="curation-list-card hero-carousal-card-height">
                    {/* Row One */}
                    <div {...dragHandleProps} className="d-flex j-space">
                        <span className="curation-title">
                            #{RAIL_LABEL[data.template]}
                        </span>

                        <div>
                            <span
                                className="cursor-pointer delete-circle-style"
                                onClick={onClickDelete}
                            >
                                {allowEdit && <DeleteIcon />}
                            </span>
                        </div>
                    </div>

                    {/* row content */}
                    {showCollectionList && (
                        <CollectionForm
                            playlist={playlistState}
                            allowEdit={allowEdit}
                            draft={collectionInfoDraft}
                            collections={data?.collections}
                            ref={collectionListRef}
                            handleCollections={handleCollections}
                        />
                    )}

                    <div {...dragHandleProps} className="mt-3 d-flex j-space">
                        {!showCollectionList && (
                            <>
                                <span
                                    onClick={onOpenCollection}
                                    className="curation-edit cursor-pointer"
                                >
                                    Edit Rail
                                </span>
                                <KeyboardArrowDownIcon
                                    className="cursor-pointer"
                                    onClick={onOpenCollection}
                                />
                            </>
                        )}
                    </div>

                    {showCollectionList && (
                        <div
                            {...dragHandleProps}
                            className="d-flex flex-column"
                        >
                            <div className="d-flex j-end">
                                {allowEdit && (
                                    <Button
                                        onClick={saveCollections}
                                        className="max-content-width float-center align-right mr-2"
                                        variant="contained"
                                        type="button"
                                    >
                                        Save Collection
                                    </Button>
                                )}
                                <KeyboardArrowUpIcon
                                    className="align-right cursor-pointer"
                                    onClick={onCloseCollection}
                                />
                            </div>
                            {errorMessage && (
                                <div className="text-danger d-flex j-end publish-error-text">
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div
                    {...dragHandleProps}
                    className="curation-list-card-container-span"
                >
                    {!hideAddButton && allowEdit && (
                        <Tooltip title="Add New Rail">
                            <span
                                className="cursor-pointer add-circle-style"
                                onClick={onClickAdd}
                            >
                                <AddCircleOutlineIcon />
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>
        );
    }
);

export default CollectionsContentCurationCard;
